import { useState, useRef, useEffect, useCallback } from 'react';
import type { CSSProperties } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, profileService } from '../services/api';
import type { UserProfileDto } from '../types';

// ====================================================================
//  VoiceChatbotPage
//  Voice-enabled chatbot for Registration, PostJob, and SubmitQuote.
//
//  Speech pipeline:  Web Speech API STT  LLM  Web Speech API TTS
//  Real-time status: SignalR /hubs/voice  (graceful fallback to HTTP)
//
//  Query params:
//    role   Customer | Driver | Workshop | Haulier
//    flow   Registration | PostJob | SubmitQuote
// ====================================================================

type VoicePhase = 'idle' | 'recording' | 'thinking' | 'playing';

interface VoiceMessage {
  id: string;
  role: 'user' | 'bot';
  text: string;
  timestamp: Date;
}

interface ExtractedFields { [key: string]: unknown; }

interface VoiceChatbotResponse {
  sessionId: string;
  transcribedText: string;
  botReplyText: string;
  botReplyAudioB64?: string;
  isComplete: boolean;
  extractedFields: ExtractedFields;
  profileCompleteness: number;
  requiresDocumentUpload?: boolean;
  documentUploadField?: string;
  documentUploadLabel?: string;
}

//  Constants 

const PIPELINE_STAGES: { phase: VoicePhase; icon: string; label: string }[] = [
  { phase: 'recording', icon: '', label: 'Listening' },
  { phase: 'thinking',  icon: '', label: 'Processing' },
  { phase: 'playing',   icon: '', label: 'Speaking' },
];

const PHASE_LABELS: Record<VoicePhase, string> = {
  idle:      '',
  recording: 'Listening click  to stop speaking',
  thinking:  'Processing your request',
  playing:   'Speaking reply',
};

const INSURANCE_FIELDS = ['hsCompliance', 'goodsInTransit', 'publicLiability'] as const;
type InsuranceField = typeof INSURANCE_FIELDS[number];

const INSURANCE_LABELS: Record<InsuranceField, string> = {
  hsCompliance:    'H&S Compliance Document',
  goodsInTransit:  'Goods in Transit Insurance',
  publicLiability: 'Public Liability Insurance (min £1m)',
};

const api = axios.create({ baseURL: import.meta.env.VITE_API_BASE_URL ?? '' });

// Preferred female voice names across Edge, Chrome, macOS, and Android.
// "female" keyword covers Chrome ("Google UK English Female").
// Named list covers Edge online natural voices and macOS system voices.
const FEMALE_VOICE_HINTS = [
  'female',
  'Sonia', 'Aria', 'Libby', 'Maisie', 'Jenny', 'Emma', 'Ava', 'Hazel',
  'Zira', 'Clara', 'Emily',
  'Samantha', 'Karen', 'Moira', 'Tessa', 'Fiona', 'Victoria', 'Susan', 'Nicky',
];

function pickFemaleVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  const en = voices.filter(v => v.lang.startsWith('en'));
  // Prefer en-GB, then en-US, then any English voice.
  const preferred = [
    ...en.filter(v => v.lang === 'en-GB'),
    ...en.filter(v => v.lang === 'en-US'),
    ...en,
  ];
  for (const hint of FEMALE_VOICE_HINTS) {
    const match = preferred.find(v => v.name.toLowerCase().includes(hint.toLowerCase()));
    if (match) return match;
  }
  return null;
}

//  Helper components 

function PipelineIndicator({ phase }: { phase: VoicePhase }) {
  if (phase === 'idle') return null;
  const activeIdx = PIPELINE_STAGES.findIndex(s => s.phase === phase);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 16px', background: '#ecfdf5', borderTop: '1px solid #a7f3d0', flexWrap: 'wrap' }}>
      {PIPELINE_STAGES.map((s, i) => (
        <span key={s.phase} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{
            fontSize: 11,
            padding: '2px 8px',
            borderRadius: 999,
            background: i === activeIdx ? '#047857' : i < activeIdx ? '#dcfce7' : '#e5e7eb',
            color: i === activeIdx ? '#fff' : i < activeIdx ? '#166534' : '#9ca3af',
            fontWeight: i === activeIdx ? 700 : 400,
            whiteSpace: 'nowrap' as const,
          }}>
            {s.icon} {s.label}
          </span>
          {i < PIPELINE_STAGES.length - 1 && (
            <span style={{ color: '#d1d5db', fontSize: 10 }}></span>
          )}
        </span>
      ))}
    </div>
  );
}


//  Helpers 

function useQueryParam(name: string, defaultVal: string): string {
  const params = new URLSearchParams(window.location.search);
  return params.get(name) ?? defaultVal;
}

function generateId(): string {
  return Math.random().toString(36).slice(2);
}

function getWelcomeText(role: string, flow: string): string {
  if (flow === 'PostJob')     return "Hi! Tell me about the delivery you need and I'll help you post a job.";
  if (flow === 'SubmitQuote') return "Hi! Ready to submit a quote? Tell me the job reference and your price.";
  const greetings: Record<string, string> = {
    Driver:   "Welcome to TransitDeal! I'll guide you through driver registration including your insurance documents. Let's start  what's your full name?",
    Workshop:   "Welcome! Let's get your workshop listed. What's your business name and postcode?",
    Haulier:  "Welcome! I'll help register your logistics company. What's your company name?",
    Customer: "Welcome to TransitDeal! Let's create your account. What's your first name?",
  };
  return greetings[role] ?? greetings.Customer;
}

function formatKey(k: string): string {
  return k.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase());
}

//  Main component 

export default function VoiceChatbotPage() {
  const role      = useQueryParam('role', 'Customer');
  const flow      = useQueryParam('flow', 'Registration');
  const sessionId = useRef(generateId());

  const [messages, setMessages]         = useState<VoiceMessage[]>([]);
  const [extractedFields, setExtracted] = useState<ExtractedFields>({});
  const [completeness, setCompleteness] = useState(0);
  const [isComplete, setIsComplete]     = useState(false);
  const [textInput, setTextInput]       = useState('');
  const [recording, setRecording]       = useState(false);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { getToken, account } = useAppAuth();

  const { data: profileData } = useQuery({
    queryKey: ['profile'],
    queryFn: () => profileService(createApiClient(getToken)).getProfile(),
    enabled: !!account,
  });

  const [audioEnabled, setAudioEnabled] = useState(() => {
    const cached = queryClient.getQueryData<UserProfileDto>(['profile']);
    return cached?.voiceChatEnabled ?? false;
  });

  // Sync audioEnabled from profile once it resolves  handles the case where
  // the React Query cache is cold when this page opens (e.g. direct navigation).
  const audioSynced = useRef(false);
  useEffect(() => {
    if (profileData !== undefined && !audioSynced.current) {
      audioSynced.current = true;
      setAudioEnabled(profileData.voiceChatEnabled ?? false);
    }
  }, [profileData]);
  const [phase, setPhase]   = useState<VoicePhase>('idle');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  const [pendingUploadField, setPendingUploadField] = useState<string | null>(null);
  const [pendingUploadLabel, setPendingUploadLabel] = useState<string | null>(null);
  const [uploadedDocs, setUploadedDocs] = useState<Record<string, boolean>>({});

  const recognitionRef    = useRef<SpeechRecognition | null>(null);
  const sessionCounterRef = useRef(0);
  const transcriptRef     = useRef('');
  const voicesRef         = useRef<SpeechSynthesisVoice[]>([]);
  const messagesBoxRef    = useRef<HTMLDivElement>(null);
  const fileInputRef      = useRef<HTMLInputElement>(null);
  const [liveText, setLiveText] = useState('');

  // Scroll only within the messages box, not the whole page.
  useEffect(() => {
    const box = messagesBoxRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [messages]);

  // Single-tab audio: broadcast claim so other open tabs mute themselves
  useEffect(() => {
    if (!('BroadcastChannel' in window)) return;
    const channel = new BroadcastChannel('transit-voice');
    const tabId = sessionId.current;
    channel.postMessage({ type: 'voice-tab-claim', tabId });
    channel.onmessage = (e: MessageEvent) => {
      if (e.data?.type === 'voice-tab-claim' && e.data.tabId !== tabId) {
        setAudioEnabled(false);
        window.speechSynthesis.cancel();
      }
    };
    return () => channel.close();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      recognitionRef.current?.abort();
      window.speechSynthesis.cancel();
    };
  }, []);

  // Voices load asynchronously in Chrome/Edge  keep the ref up to date.
  useEffect(() => {
    const load = () => { voicesRef.current = window.speechSynthesis.getVoices(); };
    load();
    window.speechSynthesis.addEventListener('voiceschanged', load);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', load);
  }, []);


  //  TTS playback (Web Speech API) 

  const speakText = useCallback((text: string) => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-GB';
    const femaleVoice = pickFemaleVoice(voicesRef.current);
    if (femaleVoice) utterance.voice = femaleVoice;
    utterance.onstart = () => setPhase('playing');
    utterance.onend   = () => setPhase('idle');
    utterance.onerror = () => setPhase('idle');
    window.speechSynthesis.speak(utterance);
  }, []);

  //  Response handler 

  const handleResponse = useCallback((data: VoiceChatbotResponse) => {
    setMessages(m => [...m, {
      id:        generateId(),
      role:      'bot',
      text:      data.botReplyText,
      timestamp: new Date(),
    }]);
    setExtracted(data.extractedFields ?? {});
    setCompleteness(data.profileCompleteness ?? 0);

    if (data.requiresDocumentUpload && data.documentUploadField) {
      setPendingUploadField(data.documentUploadField);
      setPendingUploadLabel(data.documentUploadLabel ?? data.documentUploadField);
    }

    if (audioEnabled) speakText(data.botReplyText);
    else setPhase('idle');

    if (data.isComplete) setIsComplete(true);
  }, [audioEnabled, speakText]);

  const handleResponseRef = useRef(handleResponse);
  useEffect(() => { handleResponseRef.current = handleResponse; }, [handleResponse]);

  // SignalR /hubs/voice is not registered on the server  removed to avoid
  // the 404HTML negotiation error flooding the console.

  //  Welcome message 

  useEffect(() => {
    const welcomeText = getWelcomeText(role, flow);
    setMessages([{ id: generateId(), role: 'bot', text: welcomeText, timestamp: new Date() }]);
    if (audioEnabled) speakText(welcomeText);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //  Text send 

  const sendText = async () => {
    const msg = textInput.trim();
    if (!msg || loading) return;
    setTextInput('');
    setError(null);
    setMessages(m => [...m, { id: generateId(), role: 'user', text: msg, timestamp: new Date() }]);
    setLoading(true);
    setPhase('thinking');
    try {
      const res = await api.post<VoiceChatbotResponse>('/api/v1/voice/text', {
        sessionId: sessionId.current, roleContext: role, flowContext: flow,
        message: msg, returnAudio: false,
      });
      handleResponseRef.current(res.data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to send message.');
      setPhase('idle');
    } finally { setLoading(false); }
  };

  //  Document upload 

  const handleDocumentUpload = async (file: File) => {
    if (!pendingUploadField) return;
    setLoading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file',         file);
      form.append('sessionId',    sessionId.current);
      form.append('documentType', pendingUploadField);
      form.append('roleContext',  role);
      form.append('flowContext',  flow);

      const res = await api.post<VoiceChatbotResponse>('/api/v1/voice/document', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadedDocs(d => ({ ...d, [pendingUploadField]: true }));
      setPendingUploadField(null);
      setPendingUploadLabel(null);
      handleResponseRef.current(res.data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Document upload failed. Please try again.');
    } finally { setLoading(false); }
  };

  //  Voice recording (Web Speech API) 

  const sendTranscript = async (transcript: string) => {
    setLoading(true);
    try {
      const res = await api.post<VoiceChatbotResponse>('/api/v1/voice/text', {
        sessionId:   sessionId.current,
        roleContext: role,
        flowContext: flow,
        message:     transcript,
        returnAudio: false,
      });
      handleResponseRef.current(res.data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to process speech.');
      setPhase('idle');
    } finally {
      setLoading(false);
    }
  };

  // Submits whatever text has accumulated in transcriptRef.
  const flushTranscript = () => {
    const transcript = transcriptRef.current.trim();
    transcriptRef.current = '';
    setLiveText('');
    if (!transcript) { setPhase('idle'); return; }
    setMessages(m => [...m, { id: generateId(), role: 'user', text: transcript, timestamp: new Date() }]);
    setPhase('thinking');
    sendTranscript(transcript);
  };

  // isRetry=true skips teardown of the previous instance (already gone)
  // and uses a longer start delay so Edge can re-open the speech service socket.
  const startRecording = (isRetry = false) => {
    if (!isRetry) {
      setError(null);
      transcriptRef.current = '';
      setLiveText('');
    }

    const SpeechRecognitionAPI = window.SpeechRecognition ?? window.webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) {
      setError('Speech recognition is not supported in this browser. Please use Chrome or Edge.');
      return;
    }

    // Each attempt gets a unique session ID  all async callbacks bail if stale.
    const mySession = ++sessionCounterRef.current;

    if (!isRetry && recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch { /* already ended */ }
      recognitionRef.current = null;
    }

    const recognition = new SpeechRecognitionAPI();
    recognition.lang = 'en-US';
    // continuous=true keeps listening until the user clicks stop, rather than
    // auto-stopping at the first pause  makes speech capture far more natural.
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      if (sessionCounterRef.current !== mySession) return;
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const text = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          transcriptRef.current += (transcriptRef.current ? ' ' : '') + text.trim();
        } else {
          interim += text;
        }
      }
      // Show live partial text so the user can see recognition is working.
      setLiveText((transcriptRef.current + (interim ? ' ' + interim : '')).trim());
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (sessionCounterRef.current !== mySession) return;
      if (event.error === 'aborted' || event.error === 'no-speech') return;

      // Edge/Chromium drops the speech service WebSocket after the first session.
      // Increment NOW so the imminent onend sees a stale session (no flicker),
      // then retry after 800 ms to re-establish the connection silently.
      if (event.error === 'network' && !isRetry) {
        const retrySession = ++sessionCounterRef.current;
        setTimeout(() => {
          if (sessionCounterRef.current === retrySession) startRecording(true);
        }, 800);
        return;
      }

      setRecording(false);
      setPhase('idle');
      const msgs: Partial<Record<string, string>> = {
        'network':               'Speech recognition failed to connect. Please try again.',
        'not-allowed':           'Microphone access was denied. Please allow microphone access in your browser settings.',
        'service-not-allowed':   'Speech recognition is blocked. Please allow microphone access in your browser settings.',
        'audio-capture':         'No microphone found. Please connect a microphone and try again.',
        'language-not-supported':'Speech recognition does not support this language.',
      };
      setError(msgs[event.error] ?? `Speech recognition error: ${event.error}. Please try again.`);
    };

    // onend fires if the browser stops recognition unexpectedly (e.g. long silence
    // timeout). For explicit user stops, stopRecording() handles this instead.
    recognition.onend = () => {
      if (sessionCounterRef.current !== mySession) return;
      setRecording(false);
      flushTranscript();
    };

    recognitionRef.current = recognition;
    setTimeout(() => {
      if (sessionCounterRef.current === mySession) {
        try { recognition.start(); } catch { /* ignore if already started */ }
      }
    }, isRetry ? 500 : 150);

    setPhase('recording');
    setRecording(true);
  };

  const stopRecording = () => {
    // Invalidate the current session so its onend doesn't double-process
    // the transcript  we handle it here directly.
    sessionCounterRef.current++;
    try { recognitionRef.current?.stop(); } catch { /* already stopped */ }
    recognitionRef.current = null;
    setRecording(false);
    flushTranscript();
  };

  const toggleRecording = () => {
    if (recording) stopRecording();
    else startRecording();
  };

  //  Completion screen 

  if (isComplete) return (
    <div style={styles.page}>
      <div style={{ ...styles.card, textAlign: 'center', maxWidth: 440 }}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>✓…</div>
        <h2 style={styles.h2}>
          {flow === 'Registration' ? 'Registration Complete!' :
           flow === 'PostJob'      ? 'Job Posted!'           :
                                     'Quote Submitted!'}
        </h2>
        <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 20 }}>
          {flow === 'Registration'
            ? 'Your account has been created. Our team will verify your documents within 24 hours.'
            : 'Your request has been processed successfully.'}
        </p>

        {role === 'Carrier' && flow === 'Registration' && (
          <div style={styles.insuranceStatus}>
            <h4 style={{ margin: '0 0 10px', fontSize: 13, color: '#374151' }}>
              Insurance documents submitted:
            </h4>
            {INSURANCE_FIELDS.map(field => (
              <div key={field} style={styles.insuranceRow}>
                <span style={{ color: uploadedDocs[field] ? '#16a34a' : '#dc2626' }}>
                  {uploadedDocs[field] ? '✓' : '✗'}
                </span>
                <span style={{ fontSize: 12, color: '#374151' }}>{INSURANCE_LABELS[field]}</span>
              </div>
            ))}
          </div>
        )}

        <div style={styles.fieldsCard}>
          <h4 style={{ margin: '0 0 10px', fontSize: 13, color: '#374151' }}>Collected information:</h4>
          {Object.entries(extractedFields)
            .filter(([, v]) => v !== null && v !== undefined && !String(v).includes('Base64'))
            .map(([k, v]) => (
              <div key={k} style={styles.fieldRow}>
                <span style={styles.fieldKey}>{formatKey(k)}</span>
                <span style={styles.fieldVal}>{String(v)}</span>
              </div>
            ))}
        </div>
        <button
          style={styles.btnPrimary}
          onClick={() => { window.location.href = flow === 'Registration' ? '/dashboard' : '/jobs'; }}
        >
          {flow === 'Registration' ? 'Go to Dashboard ' : 'View Jobs '}
        </button>
      </div>
    </div>
  );

  //  Main chat UI 

  return (
    <div style={styles.page}>
      <style>{`@keyframes pulse { from { transform: scaleY(0.4); opacity: 0.5; } to { transform: scaleY(1.4); opacity: 1; } }`}</style>
      <div style={{ ...styles.layout, flexDirection: isMobile ? 'column' : 'row' }}>

        {/* LEFT: Chat panel */}
        <div style={styles.chatPanel}>
          {/* Header */}
          <div style={styles.chatHeader}>
            <div style={styles.botAvatar}></div>
            <div>
              <strong style={{ fontSize: 15 }}>TransitDeal Assistant</strong>
              <div style={{ fontSize: 12, color: '#6b7280' }}>{flow} Â· {role}</div>
            </div>
            <button
              style={{ ...styles.audioToggle, ...(audioEnabled ? styles.audioOn : styles.audioOff) }}
              onClick={() => setAudioEnabled(v => !v)}
              title={audioEnabled ? 'Mute voice replies' : 'Enable voice replies'}
            >
              {audioEnabled ? '' : ''}
            </button>
          </div>

          {/* Pipeline indicator */}
          <PipelineIndicator phase={phase} />

          {/* Phase status label */}
          {phase !== 'idle' && (
            <div style={styles.phaseLabel}>{PHASE_LABELS[phase]}</div>
          )}

          {/* Messages */}
          <div ref={messagesBoxRef} style={{ ...styles.messages, maxHeight: isMobile ? 320 : 480 }}>
            {messages.map(msg => (
              <div
                key={msg.id}
                style={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}
              >
                <div style={{ ...styles.messageBubble, ...(msg.role === 'user' ? styles.userBubble : styles.botBubble) }}>
                  {msg.text}
                </div>
                {msg.role === 'bot' && audioEnabled && (
                  <button
                    onClick={() => speakText(msg.text)}
                    style={styles.replayBtn}
                    title="Replay audio"
                  >
                     Replay
                  </button>
                )}
              </div>
            ))}
            {loading && phase !== 'playing' && (
              <div style={{ ...styles.messageBubble, ...styles.botBubble }}>
                <span style={styles.typing}></span>
              </div>
            )}
          </div>

          {error && <div style={styles.errorBox}>{error}</div>}

          {/* Document upload prompt */}
          {pendingUploadField && (
            <div style={styles.uploadPrompt}>
              <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: '#065f46' }}>
                 Please upload: <strong>{pendingUploadLabel}</strong>
              </p>
              <p style={{ margin: '0 0 10px', fontSize: 12, color: '#6b7280' }}>
                PDF, JPG or PNG Â· Max 10MB
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
                onChange={e => e.target.files?.[0] && handleDocumentUpload(e.target.files[0])}
              />
              <button style={styles.uploadBtn} onClick={() => fileInputRef.current?.click()} disabled={loading}>
                Choose File
              </button>
              <button
                style={{ ...styles.uploadBtn, background: '#f3f4f6', color: '#374151', marginLeft: 8 }}
                onClick={() => { setPendingUploadField(null); setPendingUploadLabel(null); }}
              >
                Skip for now
              </button>
            </div>
          )}

          {/* Input row */}
          <div style={styles.inputRow}>
            {recording ? (
              <div style={styles.waveformWrap}>
                {[0, 1, 2, 3, 4].map(i => (
                  <div key={i} style={{ ...styles.pulseDot, animationDelay: `${i * 120}ms` }} />
                ))}
                <span style={{ fontSize: 12, color: '#065f46', marginLeft: 8, fontStyle: liveText ? 'normal' : 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {liveText || 'Listening'}
                </span>
              </div>
            ) : (
              <input
                style={styles.textInput}
                value={textInput}
                onChange={e => setTextInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendText()}
                placeholder="Type your answer"
                disabled={loading}
              />
            )}
            <button
              style={{ ...styles.iconBtn, background: recording ? '#ef4444' : '#f3f4f6', color: recording ? '#fff' : '#374151' }}
              onClick={toggleRecording}
              disabled={loading && !recording}
              title={recording ? 'Stop recording' : 'Click to speak'}
            >
              {recording ? '' : ''}
            </button>
            <button
              style={{ ...styles.iconBtn, background: '#047857', color: '#fff' }}
              onClick={sendText}
              disabled={loading || !textInput.trim() || recording}
              title="Send"
            >
              
            </button>
          </div>
          <p style={{ fontSize: 11, color: '#9ca3af', margin: '4px 0 8px', textAlign: 'center' }}>
            {recording ? 'Click  to stop recording' : 'Click  to speak Â· Enter to send text'}
          </p>
        </div>

        {/* RIGHT: Progress sidebar  hidden on mobile */}
        {!isMobile && <div style={styles.sidebar}>
          <h3 style={styles.sidebarTitle}>Progress</h3>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${completeness}%` }} />
          </div>
          <p style={styles.progressPct}>{completeness}% complete</p>

          {/* Engine badges */}
          <h4 style={styles.sidebarSubtitle}>Voice Engine</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={styles.engineBadge}> STT: Web Speech API</span>
            <span style={styles.engineBadge}> TTS: Web Speech API</span>
            <span style={{ ...styles.engineBadge, background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>
               Real-time: SignalR
            </span>
          </div>

          {/* Insurance checklist for Carrier registration */}
          {role === 'Carrier' && flow === 'Registration' && (
            <>
              <h4 style={styles.sidebarSubtitle}>Insurance Documents</h4>
              <div style={styles.fieldsCard}>
                {INSURANCE_FIELDS.map(field => (
                  <div key={field} style={styles.fieldRow}>
                    <span style={{ color: uploadedDocs[field] ? '#16a34a' : '#9ca3af', fontSize: 14 }}>
                      {uploadedDocs[field] ? '✓' : ''}
                    </span>
                    <span style={{ ...styles.fieldKey, color: uploadedDocs[field] ? '#111827' : '#9ca3af' }}>
                      {INSURANCE_LABELS[field]}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          <h4 style={styles.sidebarSubtitle}>Collected so far</h4>
          <div style={styles.fieldsCard}>
            {Object.keys(extractedFields).length === 0
              ? <p style={{ color: '#9ca3af', fontSize: 12 }}>No fields collected yet.</p>
              : Object.entries(extractedFields)
                  .filter(([, v]) => v !== null && v !== undefined && !String(v).includes('Base64'))
                  .map(([k, v]) => (
                    <div key={k} style={styles.fieldRow}>
                      <span style={styles.fieldKey}>{formatKey(k)}</span>
                      <span style={styles.fieldVal}>{String(v)}</span>
                    </div>
                  ))
            }
          </div>
        </div>}

      </div>
    </div>
  );
}

//  Styles 

const styles: Record<string, CSSProperties> = {
  page:           { minHeight: '100vh', background: '#f3f4f6', display: 'flex', alignItems: 'stretch', justifyContent: 'center', padding: 0 },
  layout:         { display: 'flex', width: '100%', padding: 16, gap: 16, alignItems: 'flex-start' },
  chatPanel:      { flex: 1, background: '#fff', borderRadius: 12, display: 'flex', flexDirection: 'column', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' },
  chatHeader:     { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid #f3f4f6', background: '#f9fafb' },
  botAvatar:      { width: 40, height: 40, background: '#ecfdf5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 },
  audioToggle:    { marginLeft: 'auto', background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 18 },
  audioOn:        { color: '#047857', borderColor: '#a7f3d0' },
  audioOff:       { color: '#9ca3af' },
  phaseLabel:     { padding: '4px 16px', background: '#f8fafc', borderBottom: '1px solid #f1f5f9', fontSize: 11, color: '#6b7280', fontStyle: 'italic' },
  messages:       { flex: 1, padding: 16, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 320, maxHeight: 480 },
  messageBubble:  { maxWidth: '80%', padding: '10px 14px', borderRadius: 12, fontSize: 14, lineHeight: 1.5, wordBreak: 'break-word' },
  userBubble:     { alignSelf: 'flex-end', background: '#047857', color: '#fff', borderBottomRightRadius: 4 },
  botBubble:      { alignSelf: 'flex-start', background: '#f3f4f6', color: '#111827', borderBottomLeftRadius: 4 },
  replayBtn:      { marginTop: 4, fontSize: 10, color: '#6b7280', background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '2px 8px', cursor: 'pointer' },
  typing:         { letterSpacing: 4, color: '#9ca3af' },
  uploadPrompt:   { background: '#ecfdf5', borderTop: '1px solid #a7f3d0', padding: '12px 16px' },
  uploadBtn:      { padding: '8px 16px', background: '#047857', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  inputRow:       { display: 'flex', gap: 8, padding: '12px 16px', borderTop: '1px solid #f3f4f6' },
  waveformWrap:   { flex: 1, display: 'flex', alignItems: 'center', background: '#ecfdf5', borderRadius: 8, padding: '0 12px' },
  pulseDot:       { width: 6, height: 6, borderRadius: '50%', background: '#047857', animation: 'pulse 0.8s ease-in-out infinite alternate', marginRight: 4 },
  textInput:      { flex: 1, padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none' },
  iconBtn:        { width: 42, height: 42, border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  sidebar:        { width: 280, background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', flexShrink: 0 },
  sidebarTitle:   { fontSize: 15, fontWeight: 700, color: '#111827', margin: '0 0 12px' },
  sidebarSubtitle:{ fontSize: 13, fontWeight: 600, color: '#374151', margin: '16px 0 8px' },
  engineBadge:    { display: 'inline-block', fontSize: 11, padding: '4px 10px', background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', borderRadius: 6, fontWeight: 500 },
  progressBar:    { height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' },
  progressFill:   { height: '100%', background: '#047857', borderRadius: 4, transition: 'width 0.4s ease' },
  progressPct:    { fontSize: 12, color: '#6b7280', margin: '4px 0 0', textAlign: 'right' },
  fieldsCard:     { background: '#f9fafb', borderRadius: 8, padding: 12, display: 'flex', flexDirection: 'column', gap: 6 },
  fieldRow:       { display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12 },
  fieldKey:       { color: '#6b7280', flexShrink: 0 },
  fieldVal:       { color: '#111827', fontWeight: 500, textAlign: 'right', wordBreak: 'break-all' },
  errorBox:       { background: '#fef2f2', borderTop: '1px solid #fecaca', padding: '8px 16px', color: '#dc2626', fontSize: 12 },
  card:           { background: '#fff', borderRadius: 12, padding: 32, maxWidth: 480, width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' },
  h2:             { fontSize: 20, fontWeight: 700, margin: '0 0 8px', color: '#111827' },
  insuranceStatus:{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginBottom: 16, textAlign: 'left' },
  insuranceRow:   { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, fontSize: 12 },
  btnPrimary:     { width: '100%', padding: '12px 0', background: '#047857', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 16 },
};

